import React from 'react';
import { appMarketClickOnCompleteSetup } from '@wix/bi-logger-app-market-data/v2';

import {
  AppNotificationType,
  type AppNotification as AppNotificationDef,
} from './types';
import {
  useEssentials,
  useJunkYard,
  useRouterContext,
  getRouteBiData,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';

import { EditorNotSupportedNotification } from './editor-not-supported/editor-not-supported-notification';
import { SetupIncompleteNotification } from './setup-incomplete/setup-incomplete-notification';
import { OneDependencyNotInstalledNotification } from './one-dependency-not-installed/one-dependency-not-installed-notification';
import { ManyDependenciesNoneInstalledNotification } from './many-dependencies-none-installed/many-dependencies-none-installed-notification';
import { DependencyPendingEditorNotification } from './dependency-pending-editor/dependency-pending-editor-notification';
import { PremiumSiteRequiredNotification } from './premium-site-required/premium-site-required-notification';
import { HeadlessAppInstalledNotification } from './headless-app-installed/headless-app-installed-notification';
import { AppBenefitNotification } from './app-benefit/app-benefit-notification';
import { ResellerFlowNotification } from './reseller-flow/reseller-flow-notification';
import { AppIncompatibleNotification } from './app-incompatible/app-incompatible-notification';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface AppNotificationProps {
  notification: AppNotificationDef | null;
  appPageType: AppPageType;
}

export function AppNotification({
  notification,
  appPageType,
}: AppNotificationProps) {
  const { routerData, goBackToReferrer } = useJunkYard();
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();

  if (notification?.type === AppNotificationType.HeadlessAppInstalled) {
    const { appName } = notification.data;
    return <HeadlessAppInstalledNotification appName={appName} />;
  }

  if (notification?.type === AppNotificationType.EditorNotSupported) {
    const { appName, editorType } = notification.data;
    return (
      <EditorNotSupportedNotification
        appName={appName}
        editorType={editorType}
      />
    );
  }

  if (notification?.type === AppNotificationType.PremiumSiteRequired) {
    const { app } = notification.data;
    return (
      <PremiumSiteRequiredNotification app={app} appPageType={appPageType} />
    );
  }

  if (notification?.type === AppNotificationType.SetupIncomplete) {
    const { appId, dashboardUrl, isExternalDashboard } = notification.data;
    return (
      <SetupIncompleteNotification
        onCompleteClick={() => {
          biLogger.report(
            appMarketClickOnCompleteSetup({
              app_id: appId,
              referral_info: getRouteBiData(
                routerData.route,
                routerData.prevRoute,
                goBackToReferrer?.referrerNameForBI,
              ).referralInfo,
            }),
          );
          if (isExternalDashboard) {
            router.navigateTo({
              path: RoutePath.APP_EXTERNAL_DASHBOARD,
              payload: {
                appId,
                url: dashboardUrl,
              },
            });
          } else {
            router.navigateTo({
              path: RoutePath.APP_DASHBOARD,
              payload: {
                appId,
              },
            });
          }
        }}
      />
    );
  }

  if (notification?.type === AppNotificationType.DependenciesNotInstalled) {
    const { app, appDependencies } = notification.data;
    if (appDependencies.length === 1) {
      return (
        <OneDependencyNotInstalledNotification
          app={app}
          appDependency={appDependencies[0]}
          appPageType={appPageType}
        />
      );
    }

    return (
      <ManyDependenciesNoneInstalledNotification
        app={app}
        appDependencies={appDependencies}
        appPageType={appPageType}
      />
    );
  }

  if (notification?.type === AppNotificationType.DependencyPendingEditor) {
    const { app, appDependency } = notification.data;
    return (
      <DependencyPendingEditorNotification
        app={app}
        appDependency={appDependency}
        appPageType={appPageType}
      />
    );
  }

  if (notification?.type === AppNotificationType.AppBenefit) {
    const { appName } = notification.data;
    return <AppBenefitNotification appName={appName} />;
  }

  if (notification?.type === AppNotificationType.ResellerFlow) {
    const { appName } = notification.data;
    return <ResellerFlowNotification appName={appName} />;
  }

  if (notification?.type === AppNotificationType.AppIncompatible) {
    const { appName } = notification.data;
    return (
      <AppIncompatibleNotification
        appName={appName}
        onFindSimilarAppsClick={() => {
          router.navigateTo({
            path: RoutePath.SEARCH_RESULTS,
            payload: {
              query: appName,
            },
          });
        }}
      />
    );
  }

  return null;
}
